// components/branches/config/appointment/AutoApprovalSettings.jsx
import React from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/outline";
import NumberInput from "../../../common/NumberInput";

const AutoApprovalSettings = ({ config, onChange, isEditMode }) => {
  const APPOINTMENT_TYPES = [
    "PRODUCT_CONSULTATION",
    "CUSTOM_DESIGN",
    "REPAIR_CONSULTATION",
    "APPRAISAL",
    "ENGAGEMENT_RING",
    "WEDDING_JEWELRY",
    "GENERAL_INQUIRY",
    "VIP_CLIENT",
    "COLLECTION_VIEWING",
  ];

  const handleChange = (field, value) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleConditionChange = (field, value) => {
    onChange({
      ...config,
      conditions: {
        ...config.conditions,
        [field]: value,
      },
    });
  };

  const toggleAutoApproveType = (type) => {
    const currentTypes = config.conditions?.autoApproveTypes || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    handleConditionChange("autoApproveTypes", newTypes);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Auto-Approval Settings</h3>
        <p className="text-gray-600 mb-6">
          Configure which appointments can be automatically approved without
          manual review
        </p>
      </div>

      {/* Master Toggle */}
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium">Enable Auto-Approval</h4>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Automatically approve appointments that meet specified conditions
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled || false}
                onChange={(e) => handleChange("enabled", e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>
      </div>

      {/* Conditions */}
      {config.enabled && (
        <div className="space-y-4">
          <h4 className="font-medium">Auto-Approval Conditions</h4>

          {/* Within Days */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Auto-approve appointments within
              </span>
              <div className="mt-2 flex items-center gap-2">
                {isEditMode ? (
                  <>
                    <NumberInput
                      value={config.conditions?.withinDays || 7}
                      onChange={(value) =>
                        handleConditionChange("withinDays", value)
                      }
                      min="1"
                      max="30"
                      className="w-20 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">days</span>
                  </>
                ) : (
                  <span className="font-medium">
                    {config.conditions?.withinDays || 7} days
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Appointments scheduled within this timeframe will be
                auto-approved
              </p>
            </label>
          </div>

          {/* Max Per Day */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Maximum appointments per day for auto-approval
              </span>
              <div className="mt-2 flex items-center gap-2">
                {isEditMode ? (
                  <>
                    <NumberInput
                      value={config.conditions?.maxPerDay || 20}
                      onChange={(value) =>
                        handleConditionChange("maxPerDay", value)
                      }
                      min="1"
                      max="50"
                      className="w-20 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">appointments</span>
                  </>
                ) : (
                  <span className="font-medium">
                    {config.conditions?.maxPerDay || 20} appointments
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Once this limit is reached, appointments require manual approval
              </p>
            </label>
          </div>

          {/* High Value Threshold */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                High value threshold
              </span>
              <div className="mt-2 flex items-center gap-2">
                {isEditMode ? (
                  <NumberInput
                    value={config.conditions?.highValueThreshold || 100000}
                    onChange={(value) =>
                      handleConditionChange("highValueThreshold", value)
                    }
                    min="0"
                    step="1000"
                    prefix="₹"
                    className="w-32 focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <span className="font-medium">
                    ₹
                    {(
                      config.conditions?.highValueThreshold || 100000
                    ).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Appointments with expected cart value above this require manual
                approval
              </p>
            </label>
          </div>

          {/* Checkboxes */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="requiresAvailability"
                checked={config.conditions?.requiresAvailabilityCheck || false}
                onChange={(e) =>
                  handleConditionChange(
                    "requiresAvailabilityCheck",
                    e.target.checked
                  )
                }
                disabled={!isEditMode}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="requiresAvailability" className="text-sm">
                Check staff availability before auto-approving
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="requiresAdmin"
                checked={config.conditions?.requiresAdminAssignment || false}
                onChange={(e) =>
                  handleConditionChange(
                    "requiresAdminAssignment",
                    e.target.checked
                  )
                }
                disabled={!isEditMode}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="requiresAdmin" className="text-sm">
                Require at least one staff member to be available
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="excludeFirstTime"
                checked={config.conditions?.excludeFirstTimeCustomers || false}
                onChange={(e) =>
                  handleConditionChange(
                    "excludeFirstTimeCustomers",
                    e.target.checked
                  )
                }
                disabled={!isEditMode}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="excludeFirstTime" className="text-sm">
                Exclude first-time customers from auto-approval
              </label>
            </div>
          </div>

          {/* Auto-Approve Types */}
          <div>
            <h5 className="font-medium mb-3">
              Auto-Approve These Appointment Types
            </h5>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {APPOINTMENT_TYPES.map((type) => (
                  <div key={type} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`type-${type}`}
                      checked={(
                        config.conditions?.autoApproveTypes || []
                      ).includes(type)}
                      onChange={() => toggleAutoApproveType(type)}
                      disabled={!isEditMode}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`type-${type}`} className="text-sm">
                      {type.replace(/_/g, " ")}
                    </label>
                  </div>
                ))}
              </div>
              {(config.conditions?.autoApproveTypes || []).length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  No appointment types selected. All types will require manual
                  approval.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoApprovalSettings;
